import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'src/Contexts/ToastContext';
import { useCreateAlertMutation } from 'src/queries';
import type { AlertType } from 'src/types';
import './ManualAlertOverlay.css';

interface ManualAlertOverlayProps {
  installationId: string;
  draftFocusPoint: { x: number; y: number } | null;
  onDraftClear: () => void;
  pickerActive: boolean;
  onPickerToggle: (active: boolean) => void;
  // Snapshot context — copied onto the new alert so the leakage modal renders
  // the same field the admin saw when placing the focal point.
  snapshotPoints: Array<Record<string, unknown>>;
  snapshotTimestamp: string | null;
}

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
type Severity = (typeof SEVERITIES)[number];

const ManualAlertOverlay = ({
  installationId,
  draftFocusPoint,
  onDraftClear,
  pickerActive,
  onPickerToggle,
  snapshotPoints,
  snapshotTimestamp,
}: ManualAlertOverlayProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const createAlert = useCreateAlertMutation();

  const [type, setType] = useState<AlertType>('leakage');
  const [severity, setSeverity] = useState<Severity>('critical');
  const [message, setMessage] = useState('');

  const reset = () => {
    setType('leakage');
    setSeverity('critical');
    setMessage('');
    onDraftClear();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!draftFocusPoint) return;

    createAlert.mutate(
      {
        installation_id: installationId,
        type,
        severity,
        message: message.trim() || undefined,
        timestamp: snapshotTimestamp ?? new Date().toISOString(),
        focus_point: draftFocusPoint,
        measurements: snapshotPoints,
      },
      {
        onSuccess: () => {
          toast.success(t('manualAlert.successToast'));
          onPickerToggle(false);
          reset();
        },
        onError: () => {
          toast.error(t('manualAlert.errorToast'));
        },
      }
    );
  };

  const handleCancel = () => {
    onPickerToggle(false);
    reset();
  };

  return (
    <div className="manual-alert-overlay">
      <div className="manual-alert-toolbar">
        {!pickerActive ? (
          <button
            type="button"
            className="manual-alert-toggle"
            onClick={() => onPickerToggle(true)}
          >
            {t('manualAlert.toggleOn')}
          </button>
        ) : (
          <>
            <span className="manual-alert-hint">{t('manualAlert.instructions')}</span>
            <button type="button" className="manual-alert-toggle-off" onClick={handleCancel}>
              {t('manualAlert.toggleOff')}
            </button>
          </>
        )}
      </div>

      {pickerActive && draftFocusPoint && (
        <form className="manual-alert-form" onSubmit={handleSubmit}>
          <h4 className="manual-alert-form-title">{t('manualAlert.formTitle')}</h4>

          <div className="manual-alert-field">
            <span className="manual-alert-label">{t('manualAlert.focusPoint')}</span>
            <span className="manual-alert-focus">
              ({draftFocusPoint.x.toFixed(2)}, {draftFocusPoint.y.toFixed(2)})
            </span>
          </div>

          <fieldset className="manual-alert-field">
            <legend className="manual-alert-label">{t('manualAlert.type')}</legend>
            <label className="manual-alert-radio">
              <input
                type="radio"
                name="manual-alert-type"
                value="leakage"
                checked={type === 'leakage'}
                onChange={() => setType('leakage')}
              />
              {t('alarm.type.leakage')}
            </label>
            <label className="manual-alert-radio">
              <input
                type="radio"
                name="manual-alert-type"
                value="offline"
                checked={type === 'offline'}
                onChange={() => setType('offline')}
              />
              {t('alarm.type.offline')}
            </label>
            <label className="manual-alert-radio">
              <input
                type="radio"
                name="manual-alert-type"
                value="stale"
                checked={type === 'stale'}
                onChange={() => setType('stale')}
              />
              {t('alarm.type.stale')}
            </label>
          </fieldset>

          <label className="manual-alert-field">
            <span className="manual-alert-label">{t('manualAlert.severity')}</span>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity)}
              className="manual-alert-select"
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {t(`manualAlert.severity${s[0].toUpperCase()}${s.slice(1)}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="manual-alert-field">
            <span className="manual-alert-label">{t('manualAlert.message')}</span>
            <textarea
              className="manual-alert-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
            />
          </label>

          <div className="manual-alert-actions">
            <button
              type="button"
              className="manual-alert-cancel"
              onClick={handleCancel}
              disabled={createAlert.isPending}
            >
              {t('manualAlert.cancel')}
            </button>
            <button type="submit" className="manual-alert-submit" disabled={createAlert.isPending}>
              {createAlert.isPending ? t('manualAlert.submitting') : t('manualAlert.submit')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ManualAlertOverlay;
